/**
 * Security utilities for input validation and sanitization
 */

import { logger } from '@/utils/logger';

// Input sanitization helpers
export const sanitizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

export const sanitizeUsername = (username: string): string => {
  // Remove any potentially dangerous characters, keep alphanumeric, underscore, dash
  return username.replace(/[^a-zA-Z0-9_-]/g, '').trim();
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254; // RFC 5321 limit
};

export const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }
  
  if (password.length > 128) {
    errors.push("Password must be less than 128 characters");
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }
  
  if (!/\d/.test(password)) {
    errors.push("Password must contain at least one number");
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateUsername = (username: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (username.length < 3) {
    errors.push("Username must be at least 3 characters long");
  }
  
  if (username.length > 30) {
    errors.push("Username must be less than 30 characters");
  }
  
  if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
    errors.push("Username can only contain letters, numbers, underscores, and dashes");
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Rate limiting helpers (client-side)
export const isRateLimited = (key: string, maxAttempts: number, windowMs: number): boolean => {
  try {
    const now = Date.now();
    const stored = localStorage.getItem(`rate_limit_${key}`);
    
    if (!stored) {
      localStorage.setItem(`rate_limit_${key}`, JSON.stringify({ count: 1, firstAttempt: now }));
      return false;
    }
    
    const { count, firstAttempt } = JSON.parse(stored);
    
    // Reset if window has passed
    if (now - firstAttempt > windowMs) {
      localStorage.setItem(`rate_limit_${key}`, JSON.stringify({ count: 1, firstAttempt: now }));
      return false;
    }
    
    // Check if limit exceeded
    if (count >= maxAttempts) {
      return true;
    }
    
    // Increment counter
    localStorage.setItem(`rate_limit_${key}`, JSON.stringify({ count: count + 1, firstAttempt }));
    return false;
  } catch (error) {
    logger.error("Error checking rate limit:", error);
    return false; // Fail open for rate limiting
  }
};

// Security headers for forms (CSP nonce generation)
export const generateNonce = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Secure random string generation
export const generateSecureToken = (length: number = 32): string => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// XSS protection helpers
export const escapeHtml = (unsafe: string): string => {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// Session security helpers
export const isSessionValid = (sessionData: any): boolean => {
  if (!sessionData || !sessionData.expires_at) {
    return false;
  }
  
  const expiresAt = new Date(sessionData.expires_at * 1000);
  const now = new Date();
  
  return expiresAt > now;
};

// Audit logging helper
export const logSecurityEvent = (event: string, details: Record<string, any> = {}): void => {
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    event,
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...details
  };
  
  logger.info(`[SECURITY] ${event}:`, logEntry);
  
  // In production, this would send to your security monitoring service
};
